import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Box } from "../../styled/Elements";
import ActionButtons from "./components/TableComponents";
import { NoRecord } from "../../admin/NoRecord/NoRecord";
import ReactPaginate from "react-paginate";
import { useUser } from "../../../context/userContext";

const ListTable = ({
  columns,
  rows,
  totalCount,
  perPage,
  activePage,
  setActivePage,
  onEdit,
  onDelete,
  onStatusToggle,
  onOpen,
  options,
  onInfoView,
  onViewDetails,
  onDownload,
  onInvoiceDownload,
  className,
  updateVisibilityStatus,
  typeId,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user } = useUser();

  const textCenterConstants = [
    "no_of_products",
    "no",
    "unit_of_measurement",
    "id",
  ];

  const [currentPage, setCurrentPage] = useState(activePage - 1);

  useEffect(() => {
    setCurrentPage(activePage - 1);
  }, [activePage]);

  const currentItems = rows;
  const pageCount = totalCount && perPage ? Math.ceil(totalCount / perPage) : 0;

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    setActivePage(selected + 1);
  };

  return (
    <>
      <TableWrapper>
        <Box className="table-responsive custom-scroll overflow-x-auto">
          <table className={`w-100 ${className ? className : ""}`}>
            <thead>
              <tr>
                {columns.map((col, key) => (
                  <th
                    style={{
                      textAlign:
                        textCenterConstants.includes(col.name) && "center",
                    }}
                    key={key}
                    className={`text-nowrap ${
                      col.name === "actions" && "actions-column"
                    }`}
                  >
                    {t(col.label)}
                  </th>
                ))}
              </tr>
            </thead>
            {currentItems && currentItems.length > 0 ? (
              <tbody>
                {currentItems?.map((row, index) => (
                  <tr key={index}>
                    {columns?.map((col, key) => (
                      <td
                        style={{
                          textAlign:
                            textCenterConstants.includes(col.name) && "center",
                        }}
                        key={key}
                        className={`${
                          row[col.name] &&
                          row[col.name].length > 30 &&
                          "TableColumnWidth"
                        }`}
                      >
                        {col.name === "actions" ? (
                          <ActionButtons
                            typeId={typeId}
                            updateVisibilityStatus={updateVisibilityStatus}
                            row={row}
                            options={options}
                            onEdit={() => onEdit && onEdit(row)}
                            onDelete={() => onDelete && onDelete(row)}
                            onOpen={() => onOpen && onOpen(row)}
                            onStatusToggle={() =>
                              onStatusToggle && onStatusToggle(row)
                            }
                            onDownload={() => onDownload && onDownload(row)}
                            onInvoiceDownload={() => onInvoiceDownload && onInvoiceDownload(row)}
                            onInfoView={onInfoView}
                            status={row.status}
                            onViewDetails={onViewDetails}
                            tooltipData={row.infoData}
                          />
                        ) : col.name === "id" ? (
                          typeof activePage === "number" &&
                          typeof perPage === "number" ? (
                            (activePage - 1) * perPage + index + 1
                          ) : (
                            index + 1
                          )
                        ) : col.render ? (
                          col.render(row[col.name], row, t, language, user)
                        ) : (
                          row[col.name]
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan="100%">
                    <NoRecord />
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </Box>
        {currentItems &&
          Array.isArray(currentItems) &&
          currentItems.length > 0 &&
          totalCount && (
            <PaginationContainer>
              <div className="results-info">
                <span>
                  {t("showing")}{" "}
                  <strong>{Math.min(activePage * perPage, totalCount)}</strong>{" "}
                  {t("of")} <strong>{totalCount}</strong> {t("results")}
                </span>
              </div>

              <div className="pagination-wrapper">
                <button
                  className={`page-item first-page ${
                    currentPage === 0 ? "disabled" : ""
                  }`}
                  onClick={() => handlePageClick({ selected: 0 })}
                  disabled={currentPage === 0}
                >
                  «
                </button>

                <ReactPaginate
                  breakLabel="..."
                  nextLabel="›"
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageCount={pageCount}
                  forcePage={currentPage}
                  previousLabel="‹"
                  renderOnZeroPageCount={null}
                  containerClassName="pagination"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  activeClassName="active"
                />

                <button
                  className={`page-item last-page ${
                    currentPage === pageCount - 1 ? "disabled" : ""
                  }`}
                  onClick={() => handlePageClick({ selected: pageCount - 1 })}
                  disabled={currentPage === pageCount - 1}
                >
                  »
                </button>
              </div>
            </PaginationContainer>
          )}
      </TableWrapper>
    </>
  );
};

export const TableWrapper = styled.div`
  padding: 1rem;

  table {
    border-collapse: separate;
    border-spacing: 0 4px;
    tr {
      border-radius: 10px;
    }
    thead tr {
      background-color: none;
    }

    /* RTL fallback using body class */
    body.rtl thead tr th:first-child {
      border-top-right-radius: 18px;
      border-top-left-radius: 0;
    }
    body.rtl thead tr th:last-child {
      border-top-left-radius: 18px;
      border-top-right-radius: 0;
    }
    thead tr th:first-child {
      border-top-left-radius: 18px;
    }

    thead tr th:last-child {
      border-top-right-radius: 18px;
    }

    body.rtl & {
      thead tr th:first-child {
        border-top-right-radius: 18px;
        border-top-left-radius: 0;
      }

      thead tr th:last-child {
        border-top-left-radius: 18px;
        border-top-right-radius: 0;
      }
    }

    th {
      font-weight: 600;
      font-size: 14px;
      color: var(--text);
      padding: 12px 16px;
      background-color: var(--lightGrayBg);
      border: none;
      /* text-align: center; */
    }

    .actions-column {
      width: 100px;
    }

    tbody tr {
      td {
        max-width: 400px;
        background-color: var(--white);
        border-bottom: 1px solid var(--borderLight);
        padding: 8px 16px;
        color: var(--text);
        font-size: 14px;
        vertical-align: middle;
        /* text-align: center; */
      }

      .action-buttons {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        button {
          background-color: var(--white);
          color: var(--text2);
          border: none;
          padding: 0.5rem 1rem;
          cursor: pointer;
          font-size: 14px;
          border-radius: 10px;
          border: 1px solid var(--borderLight);
          &:hover {
            color: var(--text);
            background-color: var(--borderLight);
          }
        }
      }
    }
    tbody tr:last-child td {
      border-bottom: none;
    }
    &.tableSecondary {
      border-collapse: collapse;
      border-spacing: 0 0px;
      tbody tr {
        border: 1px solid var(--borderLight);
        border-radius: 10px 0 0 10px;
        overflow: hidden;
        td {
          padding: 12px 16px;
          border: none;
        }
        td:first-child {
          border-radius: 10px 0 0 10px;
        }

        /* td:first-child {
                border-radius: 10px 0 0 10px;
            } */
      }
    }
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  background-color: var(--white);
  border-radius: 15px;
  margin-top: 12px;
  margin-right: -4px;
  margin-left: -4px;

  @media (max-width: 598px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .results-info {
    font-size: 14px;
    color: var(--text2);
    display: flex;
    align-items: center;
  }
  .results-info strong {
    color: var(--text);
  }
`;

export default ListTable;
